<template>
    <div class="template-item-edit-panel" :class="[$mq]">
            <form class="edit-form" @submit.prevent="submitUpdate">
                <header class="edit-header">
                    <h3 class="edit-title">{{ createMode ? $t('config.templates.items.add_item_form.title') : displayTitle }}</h3>
                    <p v-if="!createMode && addedByText" class="edit-subtitle">{{ addedByText }}</p>
                </header>
                <div class="form-body">
                <div v-if="createMode || !isCp" class="form-group">
                    <div v-if="createMode" class="add-item-info-banner" role="status">
                        <i class="i-andy-info-main i-andy-m add-item-info-banner__icon" aria-hidden="true"></i>
                        <p class="add-item-info-banner__text">{{ createModeInfoMessage }}</p>
                    </div>
                    <label class="form-label">{{ createMode ? $t('config.templates.items.add_item_form.item_type') : $t('config.templates.items.edit_form.item_type') }}</label>
                    <div
                        ref="itemTypeSelectRoot"
                        class="item-type-select"
                        :class="{ 'item-type-select--open': itemTypeDropdownOpen, 'item-type-select--disabled': itemTypeTriggerDisabled }"
                    >
                        <button
                            type="button"
                            class="item-type-select__trigger form-select"
                            :disabled="itemTypeTriggerDisabled"
                            :aria-expanded="itemTypeDropdownOpen ? 'true' : 'false'"
                            aria-haspopup="listbox"
                            :aria-controls="itemTypeListboxId"
                            @click="toggleItemTypeDropdown"
                        >
                            <span
                                class="item-type-select__trigger-text"
                                :class="{ 'item-type-select__trigger-text--placeholder': itemTypeTriggerIsPlaceholder }"
                            >{{ itemTypeTriggerLabel }}</span>
                            <span class="item-type-select__chevron" aria-hidden="true"></span>
                        </button>
                        <ul
                            v-show="itemTypeDropdownOpen && availableBaseItems.length > 0"
                            :id="itemTypeListboxId"
                            class="item-type-select__list"
                            role="listbox"
                            :aria-label="createMode ? $t('config.templates.items.add_item_form.item_type') : $t('config.templates.items.edit_form.item_type')"
                        >
                            <li
                                v-for="opt in availableBaseItems"
                                :key="opt.id"
                                role="option"
                                :aria-selected="String(form.type) === String(opt.id) ? 'true' : 'false'"
                                class="item-type-select__option"
                                :class="{ 'item-type-select__option--selected': String(form.type) === String(opt.id) }"
                                @click="selectItemType(opt.id)"
                            >
                                <span class="item-type-select__option-title">{{ opt.title || opt.id }}</span>
                                <div class="item-type-select__option-meta">
                                    <i :class="['i-andy-m', 'item-type-select__option-icon', getItemTypeIcon(opt.type)]" aria-hidden="true"></i>
                                    <span v-if="opt.description" class="item-type-select__option-desc">{{ opt.description }}</span>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div v-if="selectedTypeInfo && selectedTypeInfo.description" class="type-hint">
                        <i :class="['i-andy-m', typeHintIcon]"></i>
                        <span>{{ selectedTypeInfo.description }}</span>
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">{{ createMode ? $t('config.templates.items.add_item_form.name_item') : $t('config.templates.items.edit_form.item_name') }}</label>
                    <div class="input-wrapper">
                        <input
                            v-model="form.title"
                            type="text"
                            class="form-input"
                            :maxlength="maxTitleLength"
                            :disabled="saving || !canEditAll"
                            :placeholder="createMode ? $t('config.templates.items.add_item_form.placeholder_name') : undefined"
                            :aria-label="createMode ? $t('config.templates.items.add_item_form.name_item') : $t('config.templates.items.edit_form.item_name')"
                        />
                        <span class="char-count">{{ form.title.length }}/{{ maxTitleLength }}</span>
                    </div>
                </div>

                <div class="form-group">
                    <label v-if="createMode" class="form-label">{{ $t('config.templates.items.add_item_form.description') }}</label>
                    <label v-else class="form-label">{{ $t('config.templates.items.edit_form.description_label') }} <span class="label-hint">{{ $t('config.templates.items.edit_form.optional_hint') }}</span></label>
                    <div class="input-wrapper">
                        <textarea
                            v-model="form.description"
                            class="form-textarea"
                            :maxlength="maxDescLength"
                            rows="3"
                            :disabled="saving || !canEditAll"
                            :placeholder="createMode ? $t('config.templates.items.add_item_form.placeholder_description') : undefined"
                            :aria-label="createMode ? $t('config.templates.items.add_item_form.description') : $t('config.templates.items.edit_form.description_optional')"
                        ></textarea>
                        <span class="char-count">{{ form.description.length }}/{{ maxDescLength }}</span>
                    </div>
                </div>

                <div v-if="createMode && isAuto" class="identifier-create-auto-card">
                    <div class="identifier-create-auto-card__header">
                        <i class="i-andy-qr-code i-andy-m identifier-create-auto-card__icon" aria-hidden="true"></i>
                        <span class="identifier-create-auto-card__title">{{ $t('config.templates.items.add_item_form.identifier') }}</span>
                    </div>
                    <p class="identifier-create-auto-card__text">{{ $t('config.templates.items.add_item_form.indentifier_description') }}</p>
                </div>

                <template v-else-if="createMode && !isAuto">
                    <div class="form-group">
                        <label class="form-label">{{ $t('config.templates.items.add_item_form.identifier_optional') }}</label>
                        <div class="input-wrapper input-wrapper--icon">
                            <input
                                v-model="form.identifier"
                                type="text"
                                class="form-input form-input--with-icon"
                                :disabled="saving || !canEditAll"
                                :placeholder="$t('config.templates.items.add_item_form.placeholder_identifier')"
                                :aria-label="$t('config.templates.items.add_item_form.identifier_optional')"
                            />
                            <button
                                type="button"
                                class="qr-icon-btn"
                                :title="$t('config.templates.items.add_item_form.identifier')"
                                :aria-label="$t('config.templates.items.add_item_form.identifier')"
                                :disabled="!form.identifier || saving"
                                @click="openQr(form.identifier)"
                            >
                                <i class="i-andy-qr-code i-andy-m input-icon" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div class="identifier-hint-box" role="note">
                            <i class="i-andy-info-main i-andy-m identifier-hint-box__icon" aria-hidden="true"></i>
                            <span class="identifier-hint-box__text">{{ $t('config.templates.items.add_item_form.identifier_info') }}</span>
                        </div>
                    </div>
                </template>

                <div v-else-if="isManual" class="form-group">
                    <label class="form-label">{{ $t('config.templates.items.edit_form.identifier_label') }} <span class="label-hint">{{ $t('config.templates.items.edit_form.optional_hint') }}</span></label>
                    <div class="input-wrapper input-wrapper--icon">
                        <input
                            v-model="form.identifier"
                            type="text"
                            class="form-input form-input--with-icon"
                            :disabled="saving || !canEditAll"
                            :aria-label="$t('config.templates.items.edit_form.identifier')"
                        />
                        <button
                            type="button"
                            class="qr-icon-btn"
                            :title="$t('config.templates.items.edit_form.identifier')"
                            :aria-label="$t('config.templates.items.edit_form.identifier')"
                            :disabled="!form.identifier || saving"
                            @click="openQr(form.identifier)"
                        >
                            <i class="i-andy-qr-code i-andy-m input-icon" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>

                <div v-else-if="!createMode && isAuto" class="form-group identifier-readonly">
                    <i class="i-andy-qr-code i-andy-m identifier-icon" aria-hidden="true"></i>
                    <div class="identifier-content">
                        <span class="form-label">{{ $t('config.templates.items.edit_form.identifier') }}</span>
                        <p class="identifier-value">{{ identifierDisplay ? '#' + identifierDisplay : '' }}</p>
                    </div>
                    <button
                        v-if="isAuto && canOpenQr"
                        type="button"
                        class="expand-btn"
                        :title="$t('config.templates.items.edit_form.identifier')"
                        :aria-label="$t('config.templates.items.edit_form.identifier')"
                        @click="openQr()"
                    >
                        <i class="i-andy-expand i-andy-m"></i>
                    </button>
                </div>

                <div v-if="!createMode" class="form-group">
                    <div class="radio-group">
                        <span class="form-label">{{ $t('config.templates.items.edit_form.visibility') }}</span>
                        <label class="radio-label">
                            <input
                                v-model="form.apply"
                                type="radio"
                                :value="1"
                                :disabled="saving || !canEditAction"
                            />
                            <span>{{ $t('config.templates.items.edit_form.item_visible') }}</span>
                        </label>
                        <label class="radio-label">
                            <input
                                v-model="form.apply"
                                type="radio"
                                :value="0"
                                :disabled="saving || !canEditAction"
                            />
                            <span>{{ $t('config.templates.items.edit_form.item_not_visible') }}</span>
                        </label>
                    </div>
                </div>

                <button
                    v-if="!createMode && showDeleteButton"
                    type="button"
                    class="btn btn-delete"
                    :disabled="!permissions.edit_items || deleting || saving"
                    @click="confirmDelete"
                >
                    <i class="i-andy-delete i-andy-m" aria-hidden="true"></i>
                    <span class="btn-delete__text">{{ $t('config.templates.items.edit_form.delete_item') }}</span>
                </button>
                </div>

                <div class="form-actions">
                    <template v-if="createMode">
                        <button type="submit" class="btn btn-primary" :disabled="saving || !canEditAction || createSubmitBlocked">
                            {{ $t('config.templates.items.add_item_form.add') }}
                        </button>
                        <button type="button" class="btn btn-secondary" @click="$emit('close')">
                            {{ $t('config.templates.items.add_item_form.cancel') }}
                        </button>
                    </template>
                    <template v-else>
                        <button type="button" class="btn btn-secondary" @click="$emit('close')">
                            {{ $t('config.templates.items.edit_form.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary" :disabled="saving || !canEditAction">
                            {{ $t('config.templates.items.edit_form.update') }}
                        </button>
                    </template>
                </div>
            </form>

        </div>
</template>

<script>
import { openModal, closeModal } from '@/core/modalManager'
import { getFirstCustomRecord, getItemTypeIcon } from '@/utils/templateCustom'
import { normalizeFlagToBool } from '@/utils/flag'
import QRCode from 'qrcode'

const MAX_TITLE = 100
const MAX_DESC = 100

function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}


/**
 * Mapeo de type_id hijo a su categoría padre.
 * Permite que el select muestre la categoría correcta cuando el item
 * tiene un type hijo (ej: type 3 -> categoría OPTIONS id 2).
 */
const TYPE_TO_CATEGORY = {
    1: 1,
    2: 2, 3: 2,
    4: 4, 10: 4, 17: 4,
    5: 5,
    6: 6,
    7: 7, 12: 7, 13: 7,
    8: 1, 9: 1,
    11: 11,
    14: 14,
    15: 15,
    16: 16,
    18: 1,
    19: 19,
    20: 20
}

export default {
    name: 'TemplateItemEdit',
    components: {},
    props: {
        item: {
            type: Object,
            required: true
        },
        templateConfig: {
            type: Object,
            default: () => ({})
        },
        templateId: {
            type: String,
            required: true
        },
        configId: {
            type: String,
            default: 'auto'
        },
        locationId: {
            type: String,
            default: null
        },
        createMode: {
            type: Boolean,
            default: false
        },
        /**
         * Tipo de plantilla (1 = tareas, 3 = registros en customizable templates).
         * Sirve para el aviso informativo en modo alta.
         */
        templateType: {
            type: [Number, String],
            default: null
        },
        /**
         * En alta: id del checklist_item de título de sección (codificado) para colocar el ítem al final de esa sección.
         */
        sectionTitleItemId: {
            type: String,
            default: null
        }
    },
    data() {
        if (this.createMode) {
            return {
                form: {
                    title: '',
                    description: '',
                    type: '',
                    apply: 1,
                    identifier: ''
                },
                saving: false,
                deleting: false,
                maxTitleLength: MAX_TITLE,
                maxDescLength: MAX_DESC,
                typeInitialized: false,
                baseItemsLoading: true,
                itemTypeDropdownOpen: false,
                refItemIdAutoAssigned: false
            }
        }

        const customRec = getFirstCustomRecord(this.item ? this.item.custom : null)
        const titleValue = customRec && customRec.title != null
            ? customRec.title
            : (typeof this.item.custom === 'string' && this.item.custom.trim() !== '' ? this.item.custom : this.item.title)

        const descValue = customRec && customRec.description != null
            ? customRec.description
            : (this.item.description != null ? this.item.description : '')

        // Si el item tiene custom.ref_item_id (nuevo contrato), es el ID del ítem base elegido.
        // Puede ser numérico o cadena codificada; Number() sobre un ID no numérico da NaN y el watcher
        // de availableBaseItems sustituía form.type por el primer option.
        // Si no hay ref_item_id usable, caer al type del item y TYPE_TO_CATEGORY.
        let initialType = null
        if (customRec && customRec.ref_item_id != null) {
            const rawRef = customRec.ref_item_id
            const refStr = rawRef === null || rawRef === undefined ? '' : String(rawRef).trim()
            if (refStr !== '') {
                const asNum = Number(refStr)
                initialType = Number.isNaN(asNum) ? rawRef : asNum
            }
        }
        if (initialType == null && this.item.type != null) {
            const rawType = Number(this.item.type)
            initialType = TYPE_TO_CATEGORY[rawType] || rawType
        }

        const configIdNorm = this.configId != null ? String(this.configId).trim().toLowerCase() : ''
        const isManualConfig = configIdNorm === 'manual'
        const identifierValue = isManualConfig
            ? ((customRec && customRec.equipment_id) || this.item.equipment_id || '')
            : ((customRec && customRec.equipment_id) || this.item.equipment_id || this.item.qr_code || '')

        const applyValue = customRec && customRec.apply != null ? customRec.apply : this.item.apply

        return {
            form: {
                title: (typeof titleValue === 'string' ? titleValue : (this.item.title || '')).substring(0, MAX_TITLE),
                description: (typeof descValue === 'string' ? descValue : '').substring(0, MAX_DESC),
                type: initialType,
                apply: applyValue != null ? Number(applyValue) : 1,
                identifier: identifierValue
            },
            saving: false,
            deleting: false,
            maxTitleLength: MAX_TITLE,
            maxDescLength: MAX_DESC,
            typeInitialized: false,
            baseItemsLoading: true,
            itemTypeDropdownOpen: false,
            refItemIdAutoAssigned: false
        }
    },
    computed: {
        baseItems() {
            return this.$store.getters['templates/getBaseItems'] || []
        },
        availableBaseItems() {
            return this.$store.getters['templates/getBaseItemsType6'] || []
        },
        customRecord() {
            return getFirstCustomRecord(this.item ? this.item.custom : null)
        },
        hasCustom() {
            if (this.customRecord) return true
            if (typeof this.item.custom === 'string') return this.item.custom.trim() !== ''
            return false
        },
        createdBy() {
            const by = (this.customRecord && this.customRecord.created_by) != null
                ? this.customRecord.created_by
                : this.item.created_by
            return by != null ? String(by).trim().toLowerCase() : ''
        },
        showDeleteButton() {
            return this.hasCustom && this.createdBy === 'local'
        },
        permissions() {
            const cfg = this.templateConfig || {}
            const edit = normalizeFlagToBool(cfg.edit_items) || normalizeFlagToBool(cfg.conf_edit)
            const create = normalizeFlagToBool(cfg.create_items) || normalizeFlagToBool(cfg.conf_create)
            return { edit_items: edit, create_items: create }
        },
        isMandatory() {
            return normalizeFlagToBool(this.item && this.item.mandatory)
        },
        canEditAll() {
            // Modo alta: siempre permiso de creación (el slot puede traer custom del API; no mezclar con edición).
            if (this.createMode) {
                return this.permissions.create_items
            }
            // Editar un item existente se rige por edit_items, tenga o no personalización previa.
            if (this.permissions.edit_items) return true
            // Compatibilidad: si el item aún no está personalizado, create_items habilita
            // la primera personalización.
            return !this.hasCustom && this.permissions.create_items
        },
        canEditVisibilityOnly() {
            // Caso requerido: si no se puede editar/crear (canEditAll=false) y el item NO es mandatory,
            // permitir editar únicamente la visibilidad.
            return !this.canEditAll && !this.isMandatory
        },
        canEditAction() {
            if (this.createMode) {
                return this.permissions.create_items
            }
            return this.canEditAll || this.canEditVisibilityOnly
        },
        createSubmitBlocked() {
            if (!this.createMode) return false
            const t = this.form.type
            if (t === '' || t === null || t === undefined) return true
            if (!String(this.form.title || '').trim()) return true
            return false
        },
        employeeId() {
            return this.$store.getters['loginUser/getLocalEmployee'] || this.$store.getters['loginUser/getAuditorEmployee'] || null
        },
        displayTitle() {
            const t = this.form.title.trim() || this.item.title
            const d = this.form.description.trim()
            return d ? `${t} | ${d}` : t
        },
        createdByEmployeeId() {
            const raw = this.customRecord && this.customRecord.created_by_employee_id != null
                ? this.customRecord.created_by_employee_id
                : null
            const str = raw != null ? String(raw).trim() : ''
            return str !== '' ? str : null
        },
        createdByEmployeeName() {
            if (!this.createdByEmployeeId) return ''
            const getEmp = this.$store.getters['employee/getEmployee']
            const emp = typeof getEmp === 'function' ? getEmp(this.createdByEmployeeId) : null
            if (!emp) return ''
            const fullName = [emp.name, emp.surname].filter(Boolean).join(' ').trim()
            return fullName
        },
        addedByText() {
            // Requerimiento: si custom.created_by_employee_id está vacío, no mostrar subtítulo.
            // Si viene, mostrar el nombre del employee desde storage (employees store).
            if (!this.createdByEmployeeId) return ''
            if (!this.createdByEmployeeName) return ''
            return this.$t('config.templates.items.edit_form.added_by', { name: this.createdByEmployeeName })
        },
        isManual() {
            return (this.configId != null ? String(this.configId).trim().toLowerCase() : '') === 'manual'
        },
        isAuto() {
            return (this.configId != null ? String(this.configId).trim().toLowerCase() : '') === 'auto'
        },
        isCp() {
            // Mostrar "Tipo de ítem" únicamente cuando hay custom con created_by === 'local'.
            return !this.hasCustom || this.createdBy !== 'local'
        },
        qrValue() {
            // Valor que se usará para mostrar/abrir el QR en modo auto.
            // Prioridad: qr_code -> equipment_id (custom/base)
            const fromQr = this.item && this.item.qr_code != null ? String(this.item.qr_code).trim() : ''
            if (fromQr) return fromQr

            const equipment = (this.customRecord && this.customRecord.equipment_id) || this.item.equipment_id
            const fromEquipment = equipment != null ? String(equipment).trim() : ''
            if (fromEquipment) return fromEquipment

            return ''
        },
        canOpenQr() {
            return this.qrValue != null && String(this.qrValue).trim() !== ''
        },
        identifierDisplay() {
            const equipment = (this.customRecord && this.customRecord.equipment_id) || this.item.equipment_id
            if (equipment) return equipment
            if (this.item.qr_code) return this.item.qr_code
            return ''
        },
        isNewItem() {
            // Alta desde "Añadir ítem": siempre POST create aunque el slot tenga metadatos en custom.
            // Sin createMode: primer custom sobre ítem aún no personalizado → create, no update.
            console.log('isNewItem', this.createMode)
            if (this.createMode) {
                return true
            }
            return false
        },
        itemTypeListboxId() {
            return `tpl-item-type-listbox-${this._uid}`
        },
        itemTypeTriggerDisabled() {
            if (this.saving || !this.canEditAll || this.baseItemsLoading) return true
            return this.availableBaseItems.length === 0
        },
        itemTypeTriggerLabel() {
            if (this.baseItemsLoading) {
                return this.$t('config.templates.items.loading')
            }
            if (this.createMode && (this.form.type === '' || this.form.type === null || this.form.type === undefined)) {
                return this.$t('config.templates.items.add_item_form.select_type_placeholder')
            }
            const found = this.availableBaseItems.find((it) => String(it.id) === String(this.form.type))
            return found ? (found.title || String(found.id)) : ''
        },
        itemTypeTriggerIsPlaceholder() {
            if (this.baseItemsLoading || !this.createMode) return false
            return this.form.type === '' || this.form.type === null || this.form.type === undefined
        },
        createModeInfoMessage() {
            if (Number(this.templateType) === 3) {
                return this.$t('config.templates.items.add_item_form.info_banner_records')
            }
            return this.$t('config.templates.items.add_item_form.info_banner_tasks')
        },
        selectedTypeInfo() {
            const found = this.availableBaseItems.find((it) => String(it.id) === String(this.form.type))
            if (!found) return null
            return {
                label: found.title || '',
                description: found.description || '',
                type: found.type
            }
        },
        typeHintIcon() {
            const type = this.selectedTypeInfo && this.selectedTypeInfo.type
            return getItemTypeIcon(type)
        }
    },
    watch: {
        'form.type'(newType) {
            if (this.itemTypeDropdownOpen) {
                this.itemTypeDropdownOpen = false
            }
            // Solo auto-rellenar cuando el usuario cambia el selector manualmente
            if (!this.typeInitialized) return
            const found = this.availableBaseItems.find((it) => String(it.id) === String(newType))
            if (!found) return
            this.form.title = (found.title || '').substring(0, MAX_TITLE)
            this.form.description = (found.description || '').substring(0, MAX_DESC)
        },
        availableBaseItems: {
                immediate: true,
                handler(newItems) {
                    // Siempre quitar estado de carga cuando llegue la lista (aunque esté vacía)
                    this.baseItemsLoading = false
                    if (this.createMode) {
                        this.$nextTick(() => {
                            this.typeInitialized = true
                        })
                        return
                    }
                    if (!newItems.length) return
                    const exists = newItems.find((it) => String(it.id) === String(this.form.type))
                    if (!exists) {
                        // Desactivar typeInitialized antes de mutar form.type para que el watcher
                        // de form.type no sobreescriba el título/descripción del formulario,
                        // tanto en la carga inicial como en actualizaciones posteriores del store.
                        this.typeInitialized = false
                        this.form.type = newItems[0].id
                        // Evitar PUT con este ref_item_id hasta que el usuario elija en el selector
                        this.refItemIdAutoAssigned = true
                    } else {
                        this.refItemIdAutoAssigned = false
                    }
                    // $nextTick corre después del vaciado de watchers: cuando se ejecute,
                    // el watcher de form.type ya habrá retornado por typeInitialized === false.
                    this.$nextTick(() => {
                        this.typeInitialized = true
                    })
                }
            }
    },
    mounted() {
        openModal()
        this.ensureEmployeesLoaded()
        this.ensureBaseItemsLoaded()
        document.addEventListener('click', this.closeItemTypeDropdownFromOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeItemTypeDropdownFromOutside)
        closeModal()
    },
    methods: {
        getItemTypeIcon,
        toggleItemTypeDropdown() {
            if (this.itemTypeTriggerDisabled || this.availableBaseItems.length === 0) return
            this.itemTypeDropdownOpen = !this.itemTypeDropdownOpen
        },
        selectItemType(id) {
            if (this.saving || !this.canEditAll) return
            this.form.type = id
            this.refItemIdAutoAssigned = false
            this.itemTypeDropdownOpen = false
        },
        closeItemTypeDropdownFromOutside(ev) {
            const root = this.$refs.itemTypeSelectRoot
            if (!root || !this.itemTypeDropdownOpen) return
            if (!root.contains(ev.target)) {
                this.itemTypeDropdownOpen = false
            }
        },
        ensureEmployeesLoaded() {
            try {
                this.$store.dispatch('employee/loadEmployees', { force: false }).catch(() => {})
            } catch (e) {
                // no-op
            }
        },
        async ensureBaseItemsLoaded() {
            if (this.availableBaseItems.length > 0) {
                this.baseItemsLoading = false
                return
            }
            // Timeout de seguridad: si el dispatch queda bloqueado (lock de IndexedDB),
            // liberar el estado de carga para no dejar el selector colgado.
            const safetyTimer = setTimeout(() => {
                this.baseItemsLoading = false
            }, 8000)
            try {
                await this.$store.dispatch('templates/loadBaseItemsFromType6Templates')
            } catch (e) {
                // no-op: el select quedará habilitado con lista vacía en lugar de bloqueado
            } finally {
                clearTimeout(safetyTimer)
                this.baseItemsLoading = false
            }
        },
        async submitUpdate() {
            if (this.saving || !this.canEditAction) return
            if (this.createMode && this.createSubmitBlocked) {
                const typeVal = this.form.type
                if (typeVal === '' || typeVal === null || typeVal === undefined) {
                    this.$snackbar.error({
                        message: this.$t('config.templates.items.add_item_form.type_required'),
                        duration: 4000,
                        closeable: true
                    })
                } else if (!String(this.form.title || '').trim()) {
                    this.$snackbar.error({
                        message: this.$t('config.templates.items.add_item_form.name_required'),
                        duration: 4000,
                        closeable: true
                    })
                }
                return
            }
            this.saving = true
            this.$overlay.loading()
            try {
                let ok = false
                const normalizedTitle = (this.form.title.trim() || this.item.title || '').trim()
                const normalizedDescription = this.form.description.trim()
                const savedDisplayTitle = normalizedDescription ? `${normalizedTitle} | ${normalizedDescription}` : normalizedTitle

                // Si solo se permite cambiar visibilidad, no crear/editar custom: usar endpoint dedicado.
                if (!this.createMode && this.canEditVisibilityOnly && !this.canEditAll) {
                    ok = await this.$store.dispatch('templates/updateTemplateItemVisibility', {
                        templateId: this.templateId,
                        itemId: this.item.id,
                        apply: this.form.apply,
                        templateType: this.templateType
                    })
                    if (ok) this.$emit('saved', { created: false, displayTitle: savedDisplayTitle })
                    else this.$emit('close')
                    return
                }

                // ref_item_id: ID del base item seleccionado (form.type).
                // type: tipo de categoría (enum 1-20) del base item, no su database ID.
                const categoryType = this.selectedTypeInfo
                    ? this.selectedTypeInfo.type
                    : this.item.type

                // PUT: no enviar ref_item_id solo cuando el watcher sustituyó form.type por el
                // primer base item (initialType no estaba en la lista). Si el tipo ya estaba en
                // la lista (p. ej. custom.ref_item_id) o el usuario eligió en el selector,
                // refItemIdAutoAssigned es false y sí se envía.
                const updateCategoryType = this.refItemIdAutoAssigned
                    ? (this.item.type != null ? this.item.type : categoryType)
                    : (this.selectedTypeInfo
                        ? this.selectedTypeInfo.type
                        : (this.item.type != null ? this.item.type : categoryType))
                const shouldSendRefItemIdOnUpdate =
                    !this.refItemIdAutoAssigned &&
                    this.form.type !== '' &&
                    this.form.type != null &&
                    this.form.type !== undefined

                if (this.isNewItem) {
                    if (!this.canEditAll) return
                    const equipmentIdTrim = String(this.form.identifier || '').trim()
                    const equipmentPayload = {}
                    if (this.createMode) {
                        if (!this.isAuto && equipmentIdTrim) {
                            equipmentPayload.equipment_id = equipmentIdTrim
                        }
                    } else if (this.isManual) {
                        equipmentPayload.equipment_id = equipmentIdTrim
                    }
                    const sectionIdTrim =
                        this.sectionTitleItemId != null ? String(this.sectionTitleItemId).trim() : ''
                    ok = await this.$store.dispatch('templates/createTemplateItem', {
                        templateId: this.templateId,
                        templateType: this.templateType,
                        data: {
                            ref_item_id: this.form.type,
                            item_id: this.item.id,
                            title: this.form.title.trim() || this.item.title,
                            description: this.form.description.trim(),
                            type: categoryType,
                            location_id: this.locationId,
                            visible: this.form.apply === 1,
                            apply: this.form.apply,
                            employee_id: this.employeeId,
                            created_by: 'local',
                            created_by_employee_id: this.employeeId,
                            ...(sectionIdTrim !== '' ? { section_id: sectionIdTrim } : {}),
                            ...equipmentPayload
                        }
                    })
                } else {
                    ok = await this.$store.dispatch('templates/updateTemplateItem', {
                        templateId: this.templateId,
                        itemId: this.item.id,
                        templateType: this.templateType,
                        data: {
                            ...(shouldSendRefItemIdOnUpdate ? { ref_item_id: this.form.type } : {}),
                            title: this.form.title.trim() || this.item.title,
                            description: this.form.description.trim(),
                            type: updateCategoryType,
                            equipment_id: this.isManual ? this.form.identifier.trim() : undefined,
                            visible: this.form.apply === 1,
                            apply: this.form.apply,
                            employee_id: this.employeeId
                        }
                    })
                }

                if (ok) this.$emit('saved', { created: this.isNewItem, displayTitle: savedDisplayTitle })
                else this.$emit('close')
            } catch (e) {
                this.$emit('close')
            } finally {
                this.$overlay.hide()
                this.saving = false
            }
        },
        openQr(identifierValue) {
            let value = ''
            if (identifierValue !== undefined && identifierValue !== null) {
                value = String(identifierValue).trim()
            } else {
                value = this.qrValue != null ? String(this.qrValue).trim() : ''
            }
            if (!value) return
            const self = this
            QRCode.toDataURL(value, { margin: 1, width: 200 })
                .then((dataUrl) => {
                    self.$popup.media({
                        image: dataUrl,
                        caption: '#' + value,
                        textCancel: self.$t('popup.media.text_cancel'),
                        customClass: 'popup-qr-identifier'
                    })
                })
                .catch(() => {})
        },
        confirmDelete() {
            if (!this.permissions.edit_items || !this.showDeleteButton || this.deleting || this.saving) return
            const self = this
            this.$popup.confirm({
                message: this.$t('config.templates.items.delete_confirm_message', { title: escapeHtml(this.displayTitle) }),
                textSave: this.$t('config.templates.items.delete_confirm_btn'),
                textCancel: this.$t('popup.confirm.text_cancel'),
                callSave: async () => {
                    if (self.deleting) return
                    self.deleting = true
                    try {
                        const ok = await self.$store.dispatch('templates/deleteTemplateItem', {
                            templateId: self.templateId,
                            itemId: self.item.id,
                            templateType: self.templateType
                        })
                        if (ok) {
                            self.$emit('deleted', { item: self.item, displayTitle: self.displayTitle })
                        } else {
                            self.$emit('close')
                        }
                    } catch (e) {
                        self.$emit('close')
                    } finally {
                        self.deleting = false
                    }
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.template-item-edit-panel {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 0;
    padding: 0;
    min-height: 100%;

    &.desktop,
    &.landscape {
        max-width: 560px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
    }
}

.edit-form {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
    min-height: 0;
}

.form-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding-top: 8px;
}

.edit-header {
    @include display-flex();
    @include flex-direction(column);
    @include align-items(center);
    @include justify-content(center);
    margin: 0;
    padding: 0;
    text-align: center;
    width: 100%;

    .edit-title {
        font-size: 18px;
        font-family: $text-bold;
        font-weight: 700;
        color: #444;
        margin: 0 0 4px 0;
        line-height: normal;
        text-align: center;
        width: 100%;
    }

    .edit-subtitle {
        font-size: 14px;
        font-family: $text;
        font-weight: 400;
        color: #444;
        margin: 0;
        line-height: normal;
        text-align: center;
        width: 100%;
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    width: 100%;

    .form-label {
        @include font-size(sm);
        font-family: $text-bold;
        color: $neutro-s80;
        margin: 0;
        padding: 0;

        .label-hint {
            font-family: $text;
            font-weight: 400;
        }
    }

    .form-select,
    .form-input,
    .form-textarea {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        padding: 12px 8px;
        margin: 0;
        border: 1px solid $color-neutral-200;
        border-radius: 4px;
        font-family: $text;
        font-size: 18px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:disabled {
            background: $color-neutral-100;
            cursor: not-allowed;
        }
    }

    .form-select {
        padding-right: 40px;
    }

    .form-input {
        padding-right: 60px;
    }

    .form-textarea {
        resize: vertical;
        min-height: 72px;
        overflow: auto;
        text-overflow: clip;
        white-space: pre-wrap;
        padding-right: 60px;
    }

    .input-wrapper {
        position: relative;
        width: 100%;

        .char-count {
            position: absolute;
            right: 12px;
            font-size: 14px;
            font-family: $text-medium;
            font-weight: 500;
            color: #b8b8b8;
            pointer-events: none;
        }

        .form-input + .char-count {
            top: 50%;
            transform: translateY(-50%);
        }

        .form-textarea + .char-count {
            top: 12px;
        }
    }

    .form-hint {
            @include display-flex();
            @include align-items(center);
            gap: 8px;
            @include font-size(sm);
            font-family: $text;
            color: $neutro-s60;
            margin: 0;
        }

    .type-hint {
        display: flex;
        padding: 8px;
        align-items: center;
        gap: 4px;
        align-self: stretch;
        border-radius: 4px;
        background: var(--color-Neutral-100, #EDEDED);
        margin: 0;

        i {
            flex-shrink: 0;
            color: var(--color-Neutral-500, #7A7A7A);
            font-size: 14px;
        }

        span {
            flex: 1 0 0;
            color: var(--color-Neutral-500, #7A7A7A);
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .add-item-info-banner {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        padding: 8px;
        margin: 0 0 4px 0;
        width: 100%;
        box-sizing: border-box;
        border-radius: 8px;
        background: #e8edf9;
        border: 1px solid #e8edf9;

        .add-item-info-banner__icon {
            flex-shrink: 0;
            margin-top: 0;
            color: #2f55c7;
            font-size: 18px;
            line-height: 1;
        }

        .add-item-info-banner__text {
            flex: 1;
            min-width: 0;
            margin: 0;
            padding: 0;
            font-family: 'Avenir Next', $text, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.35;
            color: $neutro-s80;
        }
    }

    .item-type-select {
        position: relative;
        width: 100%;
        align-self: stretch;

        &--open .item-type-select__chevron {
            transform: rotate(180deg);
        }

        &--disabled .item-type-select__chevron {
            opacity: 0.45;
        }
    }

    .item-type-select__trigger {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
        background: #fff;
        padding-right: 8px;

        &:disabled {
            cursor: not-allowed;
        }

        &:focus {
            outline: none;
            border-color: $color-secondary-500;
            background: $color-secondary-50;
        }
    }

    .item-type-select--open .item-type-select__trigger {
        border-color: $color-secondary-500;
        background: $color-secondary-50;
    }

    .item-type-select__trigger-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: $text;
        color: $neutro-s80;

        &--placeholder {
            color: #b8b8b8;
        }
    }

    .item-type-select__chevron {
        margin-left: auto;
        flex-shrink: 0;
        width: 12px;
        height: 8px;
        background: #8f959d;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
        transition: transform 0.2s ease;
    }

    .item-type-select__list {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(100% + 8px);
        z-index: 100;
        margin: 0;
        padding: 8px;
        list-style: none;
        max-height: 320px;
        overflow-y: auto;
        background: #fff;
        border: 1px solid $neutro-t30;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        box-sizing: border-box;
    }

    .item-type-select__option {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        padding: 8px;
        margin: 0;
        cursor: pointer;
        background: #fff;

        &:nth-child(even) {
            background: $color-neutral-50;
        }

        &:hover,
        &:focus {
            background: #eef1f4;
        }

        &--selected,
        &--selected:nth-child(even) {
            background: rgba($main, 0.06);
        }
    }

    .item-type-select__option-icon {
        flex-shrink: 0;
        margin-top: 0;
        font-size: 16px;
        line-height: 1;
        color: $neutro-s60;
    }

    .item-type-select__option-meta {
        flex: 1;
        width: 100%;
        min-width: 0;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 8px;
    }

    .item-type-select__option-title {
        font-family: 'Avenir Next LT Pro', $text-bold, sans-serif;
        font-size: 16px;
        font-weight: 700;
        line-height: 1.25;
        color: var(--color-Neutral-700, #444);
    }

    .item-type-select__option-desc {
        flex: 1;
        min-width: 0;
        font-family: 'Avenir Next LT Pro', $text, sans-serif;
        font-size: 12px;
        font-weight: 400;
        line-height: 1.3;
        color: $neutro-s60;
    }
}

.input-wrapper--icon {
    .form-input--with-icon {
        padding-right: 44px;
    }

    .qr-icon-btn {
        @include interaction();
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        padding: 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: $neutro-s60;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .input-icon {
            display: block;
            color: inherit;
        }
    }
}

.identifier-readonly {
    @include display-flex();
    @include align-items(center);
    flex-direction: row;
    gap: 12px;
    padding: 12px;
    background: #fff;
    border: 1px solid $neutro-t30;
    @include border-radius(4px);

    .identifier-icon {
        flex-shrink: 0;
        color: $neutro-s60;
        font-size: 40px;
        width: 40px;
        height: 40px;
        line-height: 1;
    }

    .identifier-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;

        .form-label {
            margin: 0;
            padding: 0;
            font-size: 16px;
            line-height: 1.2;
        }

        .identifier-value {
            font-size: 16px;
            font-family: $text;
            color: $neutro-s80;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1.2;
            word-break: break-all;
        }
    }

    .expand-btn {
        @include interaction();
        flex-shrink: 0;
        padding: 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        color: $neutro-s60;

        i {
            font-size: 20px;
            width: 20px;
            height: 20px;
        }

        &:hover {
            color: $main;
        }
    }
}

.radio-group {
    display: flex;
    padding: 8px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;
    border-radius: 4px;
    background: #fff;

    .form-label {
        font-size: 18px;
    }
}

.radio-label {
    @include display-flex();
    @include align-items(center);
    gap: 10px;
    cursor: pointer;
    font-family: $text;
    font-size: 18px;
    color: $neutro-s80;
    margin: 0;
    padding: 0;

    input[type="radio"] {
        margin: 0;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        cursor: pointer;
        accent-color: $color-secondary-500;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    input:disabled + span {
        opacity: 0.7;
    }
}

.btn-delete {
    @include display-flex();
    @include align-items(center);
    gap: 8px;
    width: 100%;
    padding: 8px;
    border: 1px solid $color-neutral-100;
    border-radius: 4px;
    background: #fff;
    color: $color-error-500;
    font-family: $text;
    @include font-size(m);
    line-height: 1;
    cursor: pointer;

    .i-andy-delete {
        color: $color-error-600;
        line-height: 1;
    }

    .btn-delete__text {
        padding-top: 6px;
        color: $color-error-600;
        font-size: 18px;
    }

    &:hover:not(:disabled) {
        background: rgba($color-error-500, 0.04);
        border-color: $color-error-500;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.form-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: auto;
    flex-shrink: 0;
    padding-top: 24px;

    @media (min-width: 768px) {
        flex-direction: row;

        .btn {
            flex: 1;
        }
    }

    .btn {
        width: 100%;
        padding: 12px 20px;
        border-radius: 4px;
        font-family: $text-bold;
        @include font-size(m);
        cursor: pointer;
        border: none;
        text-align: center;

        &.btn-primary {
            background: $main;
            color: #fff;

            &:hover:not(:disabled) {
                opacity: 0.9;
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }

        &.btn-secondary {
            background: #fff;
            color: $neutro-s80;
            border: 1px solid $neutro-t30;

            &:hover {
                background: $color-neutral-100;
            }
        }
    }
}

.template-item-edit-panel.portrait .form-actions {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
}

.identifier-create-auto-card {
    @include display-flex();
    flex-direction: column;
    @include align-items(flex-start);
    gap: 8px;
    align-self: stretch;
    padding: 8px;
    background: var(--color-Neutral-White, #fff);
    @include border-radius(4px);
    width: 100%;
    box-sizing: border-box;

    .identifier-create-auto-card__header {
        @include display-flex();
        flex-direction: row;
        @include align-items(center);
        gap: 8px;
        align-self: stretch;
        min-width: 0;
    }

    .identifier-create-auto-card__icon {
        flex-shrink: 0;
        width: 13.333px;
        height: 13.333px;
        font-size: 13.333px;
        line-height: 1;
        color: var(--color-Neutral-700, #444);
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
    }

    .identifier-create-auto-card__title {
        margin: 0;
        padding: 0;
        color: var(--color-Neutral-700, #444);
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        font-family: $text-bold;
    }

    .identifier-create-auto-card__text {
        margin: 0;
        padding: 0;
        align-self: stretch;
        color: var(--color-Neutral-500, #7a7a7a);
        font-size: 14px;
        font-style: normal;
        font-family: $text;
        font-weight: 400;
        line-height: normal;
        letter-spacing: -0.42px;
    }
}

.identifier-hint-box {
    @include display-flex();
    flex-direction: row;
    @include align-items(center);
    gap: 8px;
    padding: 8px;
    margin: 0;
    background: var(--color-Neutral-White, #fff);
    border-radius: 4px;
    width: 100%;
    box-sizing: border-box;

    .identifier-hint-box__icon {
        flex-shrink: 0;
        width: 13.333px;
        height: 13.333px;
        font-size: 13.333px;
        line-height: 1;
        color: var(--color-Neutral-500, #7a7a7a);
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
    }

    .identifier-hint-box__text {
        flex: 1;
        min-width: 0;
        font-size: 14px;
        font-family: $text;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: -0.42px;
        color: var(--color-Neutral-500, #7a7a7a);
    }
}

</style>
